import { jsPDF } from "jspdf";
import { drawOfferDocument, isSwatchGroup } from "./drawOfferDocument";
import type { OfferCopy, OfferSpecGroup } from "./drawOfferDocument";

export type {
  OfferCopy,
  OfferSpecGroup,
  OfferSpecRow,
} from "./drawOfferDocument";
import { registerPDFFonts } from "./pdfFonts";
import {
  renderImageByHeight,
  renderImageCircle,
  renderImageCover,
  renderTrimmedScreenshot,
  type PDFBitmap,
} from "./pdfImages";
import {
  PDF_HEADER,
  PDF_IMAGE_BOX,
  PDF_IMAGE_SCALE,
  PDF_PAGE,
  PDF_PRINT_SCALE,
  SPEC_ROWS,
} from "./pdfTheme";

/**
 * The offer as this module needs it: strings, numbers and asset URLs, with
 * every catalog question already answered by the feature's domain layer. It
 * names no domain type, which is what keeps the document generator in `utils/`.
 */
export type OfferRenderInput = {
  title: string;
  copy: OfferCopy;
  /** Link the printed QR code resolves to (the AR hand-off URL). */
  arShareUrl: string;
  /** Brand mark for the header; omitted prints the header without one. */
  logoSrc?: string;
  /** PNG data URL of the 3D view, when the scene had one to give. */
  screenshotDataUrl?: string;
  groups: OfferSpecGroup[];
  /** Row artwork URLs, keyed by the row's own `key`. */
  rowImages: Record<string, string>;
  quantity: string;
  total: string;
  website?: string;
};

/* Artwork is prepared before anything is drawn, and a failure only costs the one
 * bitmap: an offer without a thumbnail is still a usable offer, while a rejected
 * promise here would mean no document at all.
 *
 * Rasterised per row variant rather than per URL, because the two variants want
 * different bitmaps out of the same asset — a colour row wants it clipped into
 * the swatch circle, an accessory row wants the full rectangle — so the walk
 * goes over the groups and not over `rowImages`. */
async function loadRowArtwork(
  groups: OfferSpecGroup[],
  rowImages: OfferRenderInput["rowImages"],
): Promise<Map<string, PDFBitmap>> {
  const loaded = await Promise.all(
    groups.flatMap((group) => {
      const circular = isSwatchGroup(group);
      return group.rows
        .filter((row) => rowImages[row.key])
        .map(async (row) => {
          const url = rowImages[row.key];
          try {
            return [
              row.key,
              circular
                ? await renderImageCircle(
                    url,
                    SPEC_ROWS.swatch.diameter,
                    PDF_PRINT_SCALE,
                  )
                : await renderImageCover(
                    url,
                    SPEC_ROWS.thumbnail.art,
                    PDF_PRINT_SCALE,
                  ),
            ] as const;
          } catch (error) {
            console.error(`[configurator] Offer artwork failed: ${url}`, error);
            return null;
          }
        });
    }),
  );

  return new Map(loaded.filter((entry) => entry !== null));
}

async function loadLogo(src: string | undefined): Promise<PDFBitmap | null> {
  if (!src) return null;
  try {
    return await renderImageByHeight(
      src,
      PDF_HEADER.logo.height,
      PDF_PRINT_SCALE,
    );
  } catch (error) {
    console.error(`[configurator] Offer logo failed: ${src}`, error);
    return null;
  }
}

async function loadProductRender(
  dataUrl: string | undefined,
): Promise<PDFBitmap | null> {
  if (!dataUrl?.startsWith("data:image")) return null;
  try {
    return await renderTrimmedScreenshot(
      dataUrl,
      PDF_IMAGE_BOX,
      PDF_IMAGE_SCALE,
    );
  } catch (error) {
    console.error("[configurator] Offer product render failed", error);
    return null;
  }
}

/** Slug of the offer's own title, used for the download's file name. */
export function slugify(title: string, fallback: string): string {
  return (
    title
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase() || fallback
  );
}

/** Builds the offer document and returns it as a blob, ready to be saved. */
export async function renderOfferPdf(input: OfferRenderInput): Promise<Blob> {
  const [logo, productRender, artwork] = await Promise.all([
    loadLogo(input.logoSrc),
    loadProductRender(input.screenshotDataUrl),
    loadRowArtwork(input.groups, input.rowImages),
  ]);

  // `compress` deflates the content streams, which is most of the difference
  // between an offer that emails cleanly and one that does not.
  const doc = new jsPDF({
    unit: PDF_PAGE.unit,
    format: PDF_PAGE.format,
    compress: true,
  });
  registerPDFFonts(doc);

  drawOfferDocument(doc, {
    title: input.title,
    copy: input.copy,
    arShareUrl: input.arShareUrl,
    logo,
    productRender,
    groups: input.groups,
    artwork,
    quantity: input.quantity,
    total: input.total,
    website: input.website,
  });

  return doc.output("blob");
}
